import { useState } from "react";
import { useTranslation } from "react-i18next";
import { resolveUrl } from "@/lib/queryClient";
import { PumpAnalysis } from "@shared/schema";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { CheckIcon, ClipboardIcon, Download, FileIcon, LinkIcon, Mail, Share2 } from "lucide-react";

interface ResultsExportProps {
  analysis: PumpAnalysis;
}

export function ResultsExport({ analysis }: ResultsExportProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [shareLink, setShareLink] = useState("");
  const [email, setEmail] = useState("");
  const [linkCopied, setLinkCopied] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  // Handle export formats
  const handleExport = async (format: 'csv' | 'excel' | 'pdf' | 'nyserda') => {
    try {
      const response = await fetch(resolveUrl(`/api/analyses/${analysis.id}/export?format=${format}`), {
        method: 'GET',
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error(`Export failed: ${response.statusText}`);
      }

      // Get filename from Content-Disposition header if available
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `ecm-analysis-${analysis.id}.${format === 'nyserda' ? 'xlsx' : format}`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      
      toast({
        title: t("export.success"),
        description: t("export.downloaded", { format: format.toUpperCase() }),
      });
    } catch (error) {
      console.error(`Error exporting as ${format}:`, error);
      toast({
        title: t("export.error"),
        description: t("export.failed", { format: format.toUpperCase() }),
        variant: "destructive",
      });
    }
  };

  // Generate and copy share link
  const generateShareLink = async () => {
    setIsSharing(true);
    try {
      const response = await fetch(resolveUrl(`/api/analyses/${analysis.id}/share`), {
        method: 'POST',
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error('Failed to generate share link');
      }
      
      const data = await response.json();
      const shareUrl = `${window.location.origin}/shared/${data.token}`;
      setShareLink(shareUrl);
      setShareDialogOpen(true);
      
      toast({
        title: t("share.success"),
        description: t("share.linkGenerated"),
      });
    } catch (error) {
      console.error('Error generating share link:', error);
      toast({
        title: t("share.error"),
        description: t("share.failed"),
        variant: "destructive",
      });
    } finally {
      setIsSharing(false);
    }
  };

  // Copy to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(shareLink)
      .then(() => {
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
      })
      .catch(err => {
        console.error('Failed to copy:', err);
        toast({
          title: t("share.copyError"),
          description: t("share.copyFailed"),
          variant: "destructive",
        });
      });
  };

  // Send email with analysis results
  const sendEmail = async () => {
    if (!email) {
      toast({
        title: t("email.validate"),
        description: t("email.enterValid"),
        variant: "destructive",
      });
      return;
    }

    setIsSendingEmail(true);
    try {
      const response = await fetch(resolveUrl(`/api/analyses/${analysis.id}/email`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ email }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to send email');
      }
      
      toast({
        title: t("email.success"),
        description: t("email.sent", { email }),
      });
      
      setEmailDialogOpen(false);
      setEmail("");
    } catch (error) {
      console.error('Error sending email:', error);
      toast({
        title: t("email.error"),
        description: t("email.failed"),
        variant: "destructive",
      });
    } finally {
      setIsSendingEmail(false);
    }
  };

  // Send to NYSERDA Queue
  const sendToNyserdaQueue = async () => {
    try {
      // Safely access recommendations if they exist in results
      let totalProjectCost = 0;
      let electricSavings = 0;
      let energyCostSavings = 0;
      let simplePayback = 0;
      
      const results: any = analysis.results;
      if (Array.isArray(results) && results.length > 0) {
        totalProjectCost = (results[0].estimatedPrice || 0) + (results[0].installationCost || 0);
        electricSavings = results[0].annualEnergySavings || 0;
        energyCostSavings = results[0].annualCostSavings || 0;
        simplePayback = results[0].paybackPeriod || 0;
      }

      const response = await fetch(resolveUrl('/api/nyserda'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          analysisId: analysis.id,
          ecmNumber: `ECM-${analysis.id}`,
          measureName: `Pump Retrofit: ${analysis.hp || '?'} HP`,
          measureDescription: `Retrofit ${analysis.manufacturer} ${analysis.model}`,
          status: analysis.status === "completed" ? "Identified" : analysis.status,
          totalProjectCost,
          electricSavings,
          energyCostSavings,
          gasSavings: 0,
          incentive: 0,
          simplePayback: parseFloat(simplePayback.toFixed(1))
        }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to add to queue');
      }
      
      toast({
        title: "Added to Queue",
        description: "Successfully added to the NYSERDA Submission Queue.",
      });
    } catch (error) {
      console.error('Error adding to queue:', error);
      toast({
        title: "Error",
        description: "Failed to add to NYSERDA Queue.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="flex flex-wrap gap-2">
      {/* Export Dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline">
            <Download className="mr-2 h-4 w-4" />
            {t("export.title")}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => handleExport('csv')}>
            <FileIcon className="mr-2 h-4 w-4" />
            {t("export.csv")}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleExport('excel')}>
            <FileIcon className="mr-2 h-4 w-4" />
            {t("export.excel")}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleExport('nyserda')}>
            <FileIcon className="mr-2 h-4 w-4 text-primary" />
            NYSERDA Compliance (Excel)
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleExport('pdf')}>
            <FileIcon className="mr-2 h-4 w-4" />
            {t("export.pdf")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Share Button */}
      <Button variant="outline" onClick={generateShareLink} disabled={isSharing}>
        <Share2 className="mr-2 h-4 w-4" />
        {isSharing ? t("share.generating") : t("share.title")}
      </Button>

      {/* Email Button */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogTrigger asChild>
          <Button variant="outline">
            <Mail className="mr-2 h-4 w-4" />
            {t("email.title")}
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("email.dialogTitle")}</DialogTitle>
            <DialogDescription>
              {t("email.dialogDescription")}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="email">{t("email.label")}</Label>
              <Input
                id="email"
                type="email"
                placeholder="name@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={sendEmail}
              disabled={isSendingEmail || !email}
            >
              {isSendingEmail ? t("email.sending") : t("email.send")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send to NYSERDA Queue Button */}
      <Button variant="outline" onClick={sendToNyserdaQueue} className="border-green-600 text-green-600 hover:bg-green-50">
        <FileIcon className="mr-2 h-4 w-4" />
        Send to NYSERDA Queue
      </Button>

      {/* Share Dialog */}
      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("share.dialogTitle")}</DialogTitle>
            <DialogDescription>
              {t("share.dialogDescription")}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="flex items-center space-x-2">
              <Input
                id="share-link"
                value={shareLink}
                readOnly
                className="flex-1"
              />
              <Button
                type="button"
                size="icon"
                onClick={copyToClipboard}
                variant="outline"
                className="flex-shrink-0"
              >
                {linkCopied ? (
                  <CheckIcon className="h-4 w-4 text-green-500" />
                ) : (
                  <ClipboardIcon className="h-4 w-4" />
                )}
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              {t("share.expiry")}
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => setShareDialogOpen(false)}
            >
              {t("share.close")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}